import { useEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X } from "lucide-react";
import { useStore } from "@/context/StoreContext";
import MediaGallery from "@/components/MediaGallery";
import ProductInfo from "@/components/ProductInfo";

export default function ProductModal() {
  const { t, selectedProduct: p, setSelectedProduct } = useStore();
  const close = () => setSelectedProduct(null);

  useEffect(() => {
    if (!p) return undefined;
    window.__lenis?.stop();
    document.body.style.overflow = "hidden";
    const onKey = (e) => e.key === "Escape" && setSelectedProduct(null);
    window.addEventListener("keydown", onKey);
    return () => {
      window.__lenis?.start();
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKey);
    };
  }, [p, setSelectedProduct]);

  return (
    <AnimatePresence>
      {p && (
        <>
          <motion.div
            data-testid="product-modal-backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={close}
            className="fixed inset-0 z-[80] bg-black/80 backdrop-blur-md"
          />
          <div className="pointer-events-none fixed inset-0 z-[90] flex items-end justify-center md:items-center md:p-6">
            <motion.div
              data-testid="product-modal"
              initial={{ opacity: 0, y: 80, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 80, scale: 0.98 }}
              transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
              className="pointer-events-auto relative w-full md:max-w-5xl"
            >
              <button
                data-testid="product-modal-close"
                onClick={close}
                aria-label={t.product.close}
                className="absolute right-4 top-4 z-10 flex h-10 w-10 items-center justify-center rounded-full border border-white/15 bg-black/60 text-white backdrop-blur-md transition-colors hover:border-ember hover:text-ember"
              >
                <X size={16} />
              </button>

              <div
                data-lenis-prevent
                className="max-h-[100dvh] overflow-y-auto overscroll-contain rounded-t-3xl border-t border-white/10 bg-ink-soft md:max-h-[90vh] md:rounded-3xl md:border"
              >
                <div className="grid grid-cols-1 md:grid-cols-[1.1fr_1fr]">
                  <div className="p-4 pt-16 md:sticky md:top-0 md:h-[90vh] md:self-start md:p-6 md:pt-6">
                    <MediaGallery key={p.id} media={p.media} name={p.name} t={t} />
                  </div>
                  <div className="p-6 pb-10 md:p-8 lg:p-10 lg:pr-16">
                    <ProductInfo key={p.id} product={p} />
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>
  );
}
